import { clamp } from 'lodash';

import { EditDragHandler } from './edit';
import { PanDragHandler } from './pan';
import { MarqueeDragHandler } from './marquee';
import { createItemSet } from '../itemSet';
import { hitTestNode } from '../visual/node';
import { rectCenter } from 'tabview/visualizations/misc/geometry';
import { isGroup, isNodeOpen } from '../util';
import { AqBlocks, BlocksFindRequestedEvent, BlocksNode } from '../types';
import { NodeHit } from '../visual/types';
import { extendSelected, idleCursor } from './util';
import { Vector } from '@ardoq/graph';

export const attachCanvasEvents = (
  component: AqBlocks,
  canvas: HTMLCanvasElement
) => {
  const dragBegin = (_e: PointerEvent) => {
    if (!component.dragHandler && !component.isReadonly) {
      const items = createItemSet(0);

      /* get the editable items in the intersection of the hovered and selected sets */

      for (const item of component.hovered.values()) {
        if (/* item is editable &&  */ component.selected.has(item)) {
          items.add(item);
        }
      }

      /* convert to a single move if there's at least one editable node item */

      const dragNode = items.node();

      if (dragNode) {
        component.dragHandler = new EditDragHandler(
          component,
          dragNode,
          NodeHit.Body
        );
      }
    }

    if (!component.dragHandler) {
      /* didn't manage to start an edit so fall back to a pan */

      component.dragHandler = new PanDragHandler(component);
    }
  };

  /* the browser fires dblclick after any two quick clicks, including ones that ended a drag or
     toggled an expander, so only plain clicks count towards a double click */
  let plainClickCount = 0;

  const onPointerDown = (e: PointerEvent) => {
    if (!e.isPrimary || e.button !== 0) {
      return;
    }

    //e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    component.cancelTooltip();
    component.cancelContextMenu();
    component.updateHovered.flush();

    const projection = component.projection;

    component.pointerViewport = [
      0.5 + Math.round(e.offsetX),
      0.5 + Math.round(e.offsetY),
    ];
    component.pointer = projection.unprojectPoint(component.pointerViewport);

    component.pointerDownViewport = [...component.pointerViewport];
    component.pointerDown = [...component.pointer];

    /* resolve any resolvable drag handlers */

    if (component.isUserSelectable) {
      /* here's how to start a marquee drag */
      /* drag start with the extend modifier on or off a selectable or selected item */
      /* drag start with the marquee modifier on or off a selectable or selected item */

      if (!component.dragHandler && e.altKey) {
        /* pointer down + alt modifier always starts a marquee drag */
        component.dragHandler = new MarqueeDragHandler(component);
        component.dragHandler.drag();
      }
    }

    if (!component.dragHandler && !component.isReadonly) {
      const resolution = 1.0 / component.projection.scale;
      let node: BlocksNode | undefined = undefined;
      let nodeHit = NodeHit.None;

      for (const item of component.selected.nodes()) {
        nodeHit = hitTestNode(item, component.pointerDown, resolution);

        if (nodeHit & NodeHit.Resize) {
          node = item;
          break;
        }
      }

      if (node) {
        /* pointer down on a selected node handle */
        component.dragHandler = new EditDragHandler(
          component,
          node,
          nodeHit & NodeHit.Resize
        );
        component.dragHandler.drag();
      }
    }

    canvas.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!e.isPrimary) {
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    // here's the thing about cancelling the tooltip right here. if the hover doesn't change then a new one won't be pended

    component.cancelContextMenu();

    const projection = component.projection;

    component.pointerViewport = [
      0.5 + Math.round(e.offsetX),
      0.5 + Math.round(e.offsetY),
    ];
    component.pointer = projection.unprojectPoint(component.pointerViewport);

    const { pointerViewport, pointerDownViewport } = component;
    const slop = 4; // pixels
    const d = Math.hypot(
      pointerViewport[0] - pointerDownViewport[0],
      pointerViewport[1] - pointerDownViewport[1]
    );

    if (
      !component.dragHandler &&
      component.pointerCapture === e.pointerId &&
      d > slop
    ) {
      /* turned into a drag */
      component.updateHovered.flush();
      dragBegin(e);
    }

    if (component.pointerCapture === e.pointerId && component.dragHandler) {
      /* is dragging */
      component.dragHandler.drag();
      canvas.style.cursor = component.dragHandler.cursor;
    } else {
      /* is idle */
      component.updateHovered();
      canvas.style.cursor = idleCursor(component);
    }
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!e.isPrimary || e.button !== 0) {
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    component.updateHovered.flush();
    component.cancelTooltip();
    component.cancelContextMenu();

    if (component.pointerCapture === e.pointerId) {
      if (component.dragHandler) {
        /* is dragging */
        component.dragHandler.done();
        component.dragHandler = null;
        plainClickCount = 0;
      } else {
        /* this is a click */

        const expanders: BlocksNode[] = [];
        const resolution = 1.0 / component.projection.scale;

        for (const item of component.hovered.nodes()) {
          if (
            isGroup(item) &&
            hitTestNode(item, component.pointerDown, resolution) &
              NodeHit.Expand
          ) {
            expanders.push(item);
          }
        }

        if (expanders.length !== 0) {
          /* clicked on an expander */
          const isOpen = isNodeOpen(expanders[0]);

          if (isOpen) {
            component.collapse(expanders);
          } else {
            component.expand(expanders);
          }

          plainClickCount = 0;
        } else {
          if (component.isUserSelectable) {
            if (e.shiftKey) {
              component.selected = extendSelected(component, component.hovered);
            } else {
              component.selected = component.hovered;
            }
          }

          plainClickCount += 1;
        }
      }

      canvas.releasePointerCapture(component.pointerCapture);
    }
  };

  const onDblClick = (e: MouseEvent) => {
    if (/*!e.isPrimary || */ e.button !== 0) {
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const isPlainDoubleClick = plainClickCount >= 2;

    plainClickCount = 0;

    if (!isPlainDoubleClick) {
      return;
    }

    component.updateHovered.flush();

    if (component.hovered.size > 0) {
      component.activate(component.hovered);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    let handled = true;

    component.updateHovered.flush();

    const key =
      (e.altKey ? 'alt+' : '') +
      (e.ctrlKey ? 'ctrl+' : '') +
      (e.metaKey ? 'meta+' : '') +
      (e.shiftKey ? 'shft+' : '') +
      e.key;

    switch (key) {
      default:
        handled = false;
        break;

      case 'Escape':
        component.cancel();
        break;
      case 'ArrowLeft':
        component.panLeft();
        break;
      case 'ArrowUp':
        component.panUp();
        break;
      case 'ArrowRight':
        component.panRight();
        break;
      case 'ArrowDown':
        component.panDown();
        break;
      case 'Home':
        component.zoomFit();
        break;

      case 'ctrl+f':
      case 'meta+f':
        handled = !component.dispatchEvent(
          new BlocksFindRequestedEvent({ graph: component.graph })
        );
        break;
      case 'PageUp':
        component.zoomIn();
        break;
      case 'PageDown':
        component.zoomOut();
        break;

      case ' ': // what does this do?
        component.toggleExpanded(component.actionGroups());
        break;

      case 'Enter': // what does this do?
        break;

      case 'shft+Enter':
        if (component.selected.size > 0) {
          component.activate(component.selected);
        }
        break;

      case 'ctrl+Enter': // context menu shortcut for the selected items
        if (component.selected.size !== 0) {
          /* display context menu at center of selection */

          const projection = component.projection;
          const window = projection.window;
          const center = rectCenter(component.selected.bounds());
          const position: Vector = [
            clamp(center[0], window[0], window[2]),
            clamp(center[1], window[1], window[3]),
          ];
          const pt = projection.projectPoint(position);
          const { left, top } = canvas.getBoundingClientRect();

          component.showContextMenu(component.selected, [
            left + pt[0],
            top + pt[1],
          ]);
        }

        break;
    }

    if (handled) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
    }
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const delta =
      e.deltaX !== 0
        ? e.deltaX
        : e.deltaY !== 0
          ? e.deltaY
          : e.deltaZ !== 0
            ? e.deltaZ
            : 0;

    component.cancelContextMenu();

    /* on Chrome/Mac, you cannot reliably distinguish a mouse wheel tick from a trackpad two-finger scroll.
    Both arrive as wheel events with deltaMode: 0, ctrlKey: false, and pixel-mode deltas. There's no clean signal. */

    if (e.ctrlKey) {
      /* quite possibly a trackpad pinch zoom */
      // these things fire at full frame rate so it's probably worth throttling or debouncing or something

      const scale = 1 + clamp(delta / 400, -0.5, 0.5);

      component.zoomBy(1 / scale);
    }

    if (!e.ctrlKey) {
      const scale = 1 + clamp(delta / 800, -0.5, 0.5);

      component.zoomBy(1 / scale, component.pointer);
    }
  };

  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    if (!component.dragHandler && component.doContextMenu) {
      if (!component.hovered.isSubsetOf(component.selected)) {
        // right clicked on something not in the selection
        component.selected = component.hovered;
        component.pendRender();
      }

      component.showContextMenu(component.selected, [e.clientX, e.clientY]);
    }
  };

  const onPointerEnter = (e: PointerEvent) => {
    const projection = component.projection;

    component.pointerViewport = [
      0.5 + Math.round(e.offsetX),
      0.5 + Math.round(e.offsetY),
    ];
    component.pointer = projection.unprojectPoint(component.pointerViewport);
    component.updateHovered();
    component.pendRender();
  };

  const onPointerLeave = (_e: PointerEvent) => {
    component.updateHovered.cancel();

    component.pointerViewport = [NaN, NaN];
    component.pointer = [NaN, NaN];
    component.updateHovered();
    component.cancelTooltip();
    component.pendRender();
  };

  canvas.onwheel = onWheel;
  canvas.onpointerdown = onPointerDown;
  canvas.onpointermove = onPointerMove;
  canvas.onpointerup = onPointerUp;
  canvas.ondblclick = onDblClick;
  canvas.onkeydown = onKeyDown;
  canvas.oncontextmenu = onContextMenu;
  canvas.onpointerenter = onPointerEnter;
  canvas.onpointerleave = onPointerLeave;
};

export const detachCanvasEvents = (
  _component: AqBlocks,
  canvas: HTMLCanvasElement
) => {
  canvas.onwheel = null;
  canvas.onpointerdown = null;
  canvas.onpointermove = null;
  canvas.onpointerup = null;
  canvas.ondblclick = null;
  canvas.onkeydown = null;
  canvas.oncontextmenu = null;
  canvas.onpointerenter = null;
  canvas.onpointerleave = null;
};
